import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import type {
  ScheduleRecentRun,
  ScheduleRunStatus,
} from '~/features/catalog-operations/types/catalog-ops-schedule.types';
import { cn } from '~/utils/cn';
import { formatDateTime, formatDurationMs } from '~/utils/date';

/** Run status → bar colour. A skipped fire is neutral, not a failure: the
 *  overlap policy dropped it on purpose. */
const STATUS_CLASS: Record<ScheduleRunStatus, string> = {
  COMPLETED: 'bg-success',
  FAILED: 'bg-error',
  CANCELLED: 'bg-error/60',
  SKIPPED: 'bg-border',
  RUNNING: 'bg-info animate-pulse',
  TRIGGERED: 'bg-info/60',
};

/**
 * The run trail on a schedule row — up to 12 bars, oldest to newest, so the
 * pattern of a flaky schedule is readable without opening it. Each bar carries
 * a tooltip with the run's time, outcome and duration.
 */
export function ScheduleRunTrail({ runs }: { runs: ScheduleRecentRun[] }) {
  const { t } = useTranslation('catalog-operations');

  if (runs.length === 0) {
    return (
      <span data-testid="schedule-run-trail-empty" className="text-muted-foreground text-sm">
        {t('schedules.list.neverRun')}
      </span>
    );
  }

  return (
    <div
      data-testid="schedule-run-trail"
      className="flex items-center gap-[3px]"
      aria-label={t('schedules.list.runTrailLabel')}
    >
      {runs.map((run, index) => (
        <Tooltip key={`${run.startedAt ?? 'unknown'}-${String(index)}`} delayDuration={150}>
          <TooltipTrigger asChild>
            <span
              data-testid={`schedule-run-trail-item-${String(index)}`}
              className={cn('h-4 w-1.5 shrink-0 rounded-sm', STATUS_CLASS[run.status])}
              aria-hidden="true"
            />
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            <p className="font-medium">{t(`schedules.runStatus.${run.status}`)}</p>
            {run.startedAt && <p>{formatDateTime(run.startedAt)}</p>}
            {run.durationMs !== null && <p>{formatDurationMs(run.durationMs)}</p>}
            {run.productsSucceeded !== null && (
              <p>
                {t('schedules.list.productsEnriched', {
                  count: run.productsSucceeded,
                })}
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}
